import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { CompanySettings, DonationSettings } from '@/types/firebase';
import { getCompanyByDonationSlug } from '@/lib/donations';
import DonationForm from '@/app/[companyName]/[slug]/components/DonationForm';

/**
 * PublicDonation serves both URL formats:
 * 1. /:companyName/:slug (primary)
 * 2. /donate/:slug (legacy/redirect)
 */
export default function PublicDonation() {
  const { companyName, slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [company, setCompany] = useState<{
    companyId: string; 
    companyName: string; 
    donationSettings: DonationSettings;
  } | null>(null);

  useEffect(() => {
    async function fetchDonationData() {
      try {
        setLoading(true);
        setError(false);
        
        // Case 1: Direct URL with company name and slug
        if (companyName && slug) {
          console.log(`Loading donation page: company=${companyName}, slug=${slug}`);
          const companyData = await getCompanyByDonationSlug(companyName);
          
          if (companyData && companyData.donationSettings.slug === slug) {
            setCompany(companyData);
            setLoading(false);
            return;
          }
        }
        
        // Case 2: Legacy URL with just slug (we need to find the company)
        if (!companyName && slug) {
          console.log(`Loading legacy donation page: slug=${slug}`);
          // Find the company by donation slug
          const companiesRef = collection(db, 'companies');
          const q = query(
            companiesRef,
            where('donationSettings.slug', '==', slug),
            where('donationSettings.enabled', '==', true)
          );
          
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0];
            const companyData = doc.data() as CompanySettings;
            
            if (companyData.name) {
              // Redirect to the new URL format
              const companyNameSlug = companyData.name.toLowerCase().replace(/[^a-z0-9]/g, '');
              navigate(`/${companyNameSlug}/${slug}`, { replace: true });
              return;
            }
          }
        }
        
        // If we get here, no valid donation page was found
        setError(true);
        setLoading(false);
      } catch (error) {
        console.error('Error loading donation page:', error);
        setError(true);
        setLoading(false);
      }
    }
    
    fetchDonationData();
  }, [companyName, slug, navigate]);

  if (loading) {
    return <LoadingState />;
  }

  if (error || !company) {
    return <ErrorState />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <DonationForm 
          companyId={company.companyId}
          companyName={company.companyName}
          donationSettings={company.donationSettings}
        />
      </div>
    </div>
  );
}

// Loading skeleton for the donation form
function LoadingState() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-2xl mx-auto rounded-xl bg-white shadow-md p-8">
        <div className="space-y-6 animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-24 bg-gray-200 rounded"></div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
          </div>
          
          <div className="space-y-3">
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
          </div>
          
          <div className="h-12 bg-gray-200 rounded-md"></div>
        </div>
      </div>
    </div>
  );
}

// Error state component
function ErrorState() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-2xl mx-auto rounded-xl bg-white shadow-md p-8 text-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-12 w-12 mx-auto text-red-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h2 className="mt-4 text-xl font-bold text-gray-900">Donation Page Not Found</h2>
        <p className="mt-2 text-gray-600">
          The donation page you are looking for does not exist or has been disabled.
        </p>
        <a
          href="/"
          className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          Return to Homepage
        </a>
      </div>
    </div>
  );
} 